import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FitToScreenOptions } from '@/components/canvas/store/slices/viewport.slice';
import type { Page } from '@/components/canvas/document/publicTypes';
import { relayoutPagesForVerticalZoom } from '@/components/canvas/workspace/model/pageArrangement';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';

export interface UseWorkspaceFitToScreenOptions {
  workspaceRef: React.RefObject<HTMLDivElement | null>;
  stageHostRef: React.RefObject<HTMLDivElement | null>;
  page: Page | null | undefined;
  fitToScreen: (
    containerWidth: number,
    containerHeight: number,
    options?: FitToScreenOptions,
  ) => void;
  /** 首次挂载时是否适应当前激活页（默认 true；载入已存 viewport 时可 false） */
  fitCurrentPageOnMount?: boolean;
}

export function useWorkspaceFitToScreen({
  workspaceRef,
  stageHostRef,
  page,
  fitToScreen,
  fitCurrentPageOnMount = true,
}: UseWorkspaceFitToScreenOptions) {
  const [shellSize, setShellSize] = useState({ width: 0, height: 0 });
  const [fitTransition, setFitTransition] = useState(false);

  const fitToScreenRef = useRef(fitToScreen);
  fitToScreenRef.current = fitToScreen;
  const fitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fitTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 本 Workspace 实例首次就绪时适应当前激活页（切换页、点击他页不触发） */
  const initialCanvasFitDoneRef = useRef(false);

  useLayoutEffect(() => {
    const el = workspaceRef.current;
    if (!el) return;
    const update = () => setShellSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [workspaceRef, page?.id]);

  const getFitOptions = useCallback((): FitToScreenOptions => {
    const fullscreen =
      workspaceRef.current && document.fullscreenElement === workspaceRef.current;
    return fullscreen ? { noMargin: true, chromeInsets: 'fullscreen' } : { chromeInsets: 'edit' };
  }, [workspaceRef]);

  const getFitSize = useCallback((): { w: number; h: number } | null => {
    const isFullscreen =
      workspaceRef.current && document.fullscreenElement === workspaceRef.current;
    if (isFullscreen && stageHostRef.current) {
      const v = stageHostRef.current;
      if (v.clientWidth > 0 && v.clientHeight > 0) {
        return { w: v.clientWidth, h: v.clientHeight };
      }
    }
    if (shellSize.width > 0 && shellSize.height > 0) {
      return { w: shellSize.width, h: shellSize.height };
    }
    return null;
  }, [workspaceRef, stageHostRef, shellSize.width, shellSize.height]);

  useEffect(() => {
    if (!fitCurrentPageOnMount) return;
    if (initialCanvasFitDoneRef.current) return;
    if (shellSize.width <= 0 || shellSize.height <= 0) return;
    if (!page) return;
    initialCanvasFitDoneRef.current = true;

    const fitOpts = getFitOptions();
    const runFit = () => {
      fitToScreenRef.current(shellSize.width, shellSize.height, fitOpts);
    };

    runFit();

    // 首次 fit 会改 zoom，纵向布局须按新 zoom 重排页顶 y 后再 fit 一次，否则视口会跳
    const st = useCanvasStore.getState();
    if (st.workspacePageLayoutMode === 'vertical' && st.pages.length > 0) {
      const nextPages = relayoutPagesForVerticalZoom(st.pages, st.zoom);
      const yChanged = nextPages.some(
        (p, i) => p.workspaceLayout.y !== st.pages[i]?.workspaceLayout.y,
      );
      if (yChanged) {
        useCanvasStore.setState({ pages: nextPages });
        runFit();
      }
    }
  }, [fitCurrentPageOnMount, shellSize.width, shellSize.height, page?.id, page, getFitOptions]);

  const runFitWithTransition = useCallback(
    (w: number, h: number, noMargin?: boolean) => {
      setFitTransition(true);
      fitToScreenRef.current(
        w,
        h,
        noMargin
          ? {
              noMargin: true,
              chromeInsets: 'none',
              includePageChrome: false,
              maxZoom: Number.POSITIVE_INFINITY,
            }
          : getFitOptions(),
      );
      if (fitTransitionTimeoutRef.current) clearTimeout(fitTransitionTimeoutRef.current);
      fitTransitionTimeoutRef.current = setTimeout(() => {
        setFitTransition(false);
        fitTransitionTimeoutRef.current = null;
      }, 350);
    },
    [getFitOptions],
  );

  const handleFitToScreenRequest = useCallback(() => {
    const size = getFitSize();
    if (!size) return;
    fitToScreenRef.current(size.w, size.h, getFitOptions());
  }, [getFitSize, getFitOptions]);

  const contentSize = shellSize;

  return {
    contentSize,
    shellSize,
    fitTransition,
    getFitSize,
    handleFitToScreenRequest,
    runFitWithTransition,
    fitTransitionTimeoutRef,
    fitToScreenRef,
  };
}
